/**
 * Date-range types + preset → range conversion. Lives in lib (not the
 * picker component) so React Fast Refresh can swap the picker without
 * losing in-flight state. The picker imports the helpers below as a
 * sibling.
 */

export type RangePreset =
  | "today"
  | "yesterday"
  | "7d"
  | "14d"
  | "this_week"
  | "last_week"
  | "this_mo"
  | "last_mo"
  | "30d"
  | "3mo"
  // Legacy presets kept for backward compat with already-saved
  // dashboard views and bookmarked URLs. Not surfaced in the picker
  // anymore; rangeFromPreset still resolves them.
  | "4w"
  | "12w"
  | "ytd"
  | "custom";

/** How the saved date range is interpreted on subsequent loads.
 *  - `rolling`: when a saved dashboard view (or URL) is reopened, the
 *    range is recomputed relative to today's date — so "Last 7 days"
 *    stays last-7-days regardless of when you visit. The preset name
 *    is the source of truth; the stored from/to are a snapshot at
 *    save-time.
 *  - `static`:  the from/to dates are locked. Useful for sharing a
 *    link to a specific reporting period that should never shift.
 *  Default is `rolling` — matches the natural expectation behind
 *  preset names. Mode is irrelevant when preset === "custom" because
 *  there is no formula to roll forward; we treat custom as pseudo-
 *  static regardless of the selected mode. */
export type RangeMode = "rolling" | "static";

export type DateRange = { from: Date; to: Date };

export function dStart(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function startOfWeekMon(d: Date) {
  const x = dStart(d);
  const w = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - w);
  return x;
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function rangeFromPreset(id: RangePreset, today: Date): DateRange {
  const T = dStart(today);
  switch (id) {
    case "today": return { from: T, to: T };
    case "yesterday": { const y = addDays(T, -1); return { from: y, to: y }; }
    case "7d": return { from: addDays(T, -6), to: T };
    case "14d": return { from: addDays(T, -13), to: T };
    case "this_week": { const s = startOfWeekMon(T); return { from: s, to: T }; }
    case "last_week": {
      const s = addDays(startOfWeekMon(T), -7);
      return { from: s, to: addDays(s, 6) };
    }
    case "this_mo": return { from: new Date(T.getFullYear(), T.getMonth(), 1), to: T };
    case "last_mo": {
      const s = new Date(T.getFullYear(), T.getMonth() - 1, 1);
      const e = new Date(T.getFullYear(), T.getMonth(), 0);
      return { from: s, to: e };
    }
    case "30d": return { from: addDays(T, -29), to: T };
    case "3mo": {
      // 3 months back from today. We use a calendar-month subtract
      // (not 90 days) so May 3 → Feb 3 reads naturally.
      const s = new Date(T.getFullYear(), T.getMonth() - 3, T.getDate());
      return { from: s, to: T };
    }
    case "4w": return { from: addDays(T, -27), to: T };
    case "12w": return { from: addDays(T, -83), to: T };
    case "ytd": return { from: new Date(T.getFullYear(), 0, 1), to: T };
    case "custom":
    default: return { from: addDays(T, -6), to: T };
  }
}

export function rangeToApi(r: DateRange): { date_from: string; date_to: string } {
  return { date_from: toYmd(r.from), date_to: toYmd(r.to) };
}
